import { useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";
import { Table } from "@/types/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBookableZones } from "@/hooks/useBookableZones";
import { CapacityImpactDialog } from "@/components/CapacityImpactDialog";
import { checkCapacityExceeded } from "@/lib/reservationProtectionUtils";

interface TableFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table?: Table | null;
  establishmentId?: string | null;
  onSave: (data: Omit<Table, "id" | "createdAt">) => void;
}

export function TableForm({ open, onOpenChange, table, establishmentId, onSave }: TableFormProps) {
  const { zones } = useBookableZones({ establishmentId });
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(2);
  const [minCapacity, setMinCapacity] = useState(1);
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isBookable, setIsBookable] = useState(true);
  const [allowsChildren, setAllowsChildren] = useState(true);
  const [allowsStroller, setAllowsStroller] = useState(true);
  const [isTerrace, setIsTerrace] = useState(false);
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCapacityWarning, setShowCapacityWarning] = useState(false);
  const [exceededReservations, setExceededReservations] = useState<any[]>([]);
  const [pendingSave, setPendingSave] = useState<Omit<Table, "id" | "createdAt"> | null>(null);

  useEffect(() => {
    if (table) {
      setName(table.name);
      setCapacity(table.capacity);
      setMinCapacity(table.minCapacity ?? 1);
      setNotes(table.notes || "");
      setIsActive(table.isActive);
      setIsBookable(table.isBookable ?? true);
      setAllowsChildren(table.allowsChildren ?? true);
      setAllowsStroller(table.allowsStroller ?? true);
      setIsTerrace(table.isTerrace ?? false);
      setZoneId(table.zoneId ?? null);
    } else {
      setName("");
      setCapacity(2);
      setMinCapacity(1);
      setNotes("");
      setIsActive(true);
      setIsBookable(true);
      setAllowsChildren(true);
      setAllowsStroller(true);
      setIsTerrace(false);
      setZoneId(null);
    }
    setErrors({});
    setShowCapacityWarning(false);
    setExceededReservations([]);
    setPendingSave(null);
  }, [table, open]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Le nom est requis";
    } else if (name.length > 50) {
      newErrors.name = "Le nom ne peut pas dépasser 50 caractères";
    }

    if (capacity < 1 || capacity > 20) {
      newErrors.capacity = "La capacité doit être entre 1 et 20 places";
    }

    if (minCapacity < 1 || minCapacity > capacity) {
      newErrors.minCapacity = `Le minimum doit être entre 1 et ${capacity}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const data: Omit<Table, "id" | "createdAt"> = {
      name: name.trim(),
      capacity,
      minCapacity,
      notes: notes.trim() || undefined,
      isActive,
      isBookable,
      allowsChildren,
      allowsStroller,
      isTerrace,
      zoneId,
    };

    // Si on modifie une table existante et que la capacité diminue
    if (table && capacity < table.capacity) {
      try {
        const result = await checkCapacityExceeded(table.id, capacity, false);
        
        if (result.hasExceeded) {
          // Afficher le dialogue d'avertissement
          setExceededReservations(result.exceededReservations);
          setPendingSave(data);
          setShowCapacityWarning(true);
          return;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de capacité:', error);
        // En cas d'erreur, on continue quand même
      }
    }

    // Sauvegarder directement
    onSave(data);
    onOpenChange(false);
  };

  const handleConfirmCapacityChange = () => {
    if (pendingSave) {
      onSave(pendingSave);
      onOpenChange(false);
      setShowCapacityWarning(false);
      setPendingSave(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {table ? "Modifier la table" : "Ajouter une table"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la table *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Table 1, Table Terrasse A"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacity">Nombre de places *</Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => setCapacity(Math.max(1, capacity - 1))}
                  disabled={capacity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-semibold">{capacity}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => setCapacity(Math.min(20, capacity + 1))}
                  disabled={capacity >= 20}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.capacity && (
                <p className="text-sm text-destructive">{errors.capacity}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="minCapacity">Capacité minimum</Label>
              <p className="text-xs text-muted-foreground">
                Nombre minimum de personnes pour être assigné à cette table automatiquement
              </p>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => setMinCapacity(Math.max(1, minCapacity - 1))}
                  disabled={minCapacity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-semibold">{minCapacity}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => setMinCapacity(Math.min(capacity, minCapacity + 1))}
                  disabled={minCapacity >= capacity}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.minCapacity && (
                <p className="text-sm text-destructive">{errors.minCapacity}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: En réparation, Vue sur jardin..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <Label htmlFor="active">Table active</Label>
              <Switch
                id="active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="space-y-1">
                <Label htmlFor="bookable">Réservable automatiquement</Label>
                <p className="text-xs text-muted-foreground">
                  Si désactivé, cette table sera "hors réservation" et ne sera proposée que par assignation manuelle
                </p>
              </div>
              <Switch
                id="bookable"
                checked={isBookable}
                onCheckedChange={setIsBookable}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t">
              <div className="space-y-1">
                <Label htmlFor="allowsChildren">Accueille des enfants</Label>
                <p className="text-xs text-muted-foreground">
                  Si désactivé, cette table ne sera pas proposée pour les réservations avec enfants
                </p>
              </div>
              <Switch
                id="allowsChildren"
                checked={allowsChildren}
                onCheckedChange={setAllowsChildren}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t">
              <div className="space-y-1">
                <Label htmlFor="allowsStroller">Accueille des poussettes</Label>
                <p className="text-xs text-muted-foreground">
                  Si désactivé, cette table ne sera pas proposée pour les réservations avec poussette
                </p>
              </div>
              <Switch
                id="allowsStroller"
                checked={allowsStroller}
                onCheckedChange={setAllowsStroller}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t">
              <div className="space-y-1">
                <Label htmlFor="isTerrace">🌿 Table en terrasse</Label>
                <p className="text-xs text-muted-foreground">
                  Si activé, cette table sera identifiée comme terrasse pour les préférences clients
                </p>
              </div>
              <Switch
                id="isTerrace"
                checked={isTerrace}
                onCheckedChange={setIsTerrace}
              />
            </div>

            {zones.length > 0 && (
              <div className="space-y-2 py-2 border-t">
                <Label htmlFor="zoneId">🏷️ Zone réservable</Label>
                <p className="text-xs text-muted-foreground">
                  Rattache cette table à une zone nommée (ex: Bistrot, Salon)
                </p>
                <Select
                  value={zoneId ?? "__none__"}
                  onValueChange={(v) => setZoneId(v === "__none__" ? null : v)}
                >
                  <SelectTrigger id="zoneId">
                    <SelectValue placeholder="Sans zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sans zone</SelectItem>
                    {zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-sm border"
                            style={{ backgroundColor: z.color }}
                          />
                          {z.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit}>
              {table ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CapacityImpactDialog
        open={showCapacityWarning}
        onOpenChange={setShowCapacityWarning}
        tableName={table?.name || name}
        oldCapacity={table?.capacity || 0}
        newCapacity={capacity}
        exceededReservations={exceededReservations}
        onConfirm={handleConfirmCapacityChange}
      />
    </>
  );
}
